import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  BanknotesIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  obtenerEgresos,
  registrarEgresoFinanciero,
  editarEgreso,
  eliminarEgreso,
  marcarEgresoPagado,
} from '../api/backend_v2';
import { formatearMoneda } from '../utils/moneda';

const TIPOS = ['efectivo', 'cheque', 'transferencia', 'a_pagar_efet', 'debito', 'credito', 'otro'];

const TIPO_LABELS = {
  efectivo: 'Efectivo',
  cheque: 'Cheque',
  transferencia: 'Transferencia',
  a_pagar_efet: 'A pagar (efect.)',
  debito: 'Débito',
  credito: 'Crédito',
  otro: 'Otro',
};
const tipoLabel = (t) => TIPO_LABELS[t] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : '');

const FORM_VACIO = {
  fecha: new Date().toISOString().slice(0, 10),
  importe: '',
  tipo: 'efectivo',
  receptor: '',
  fecha_cobro: '',
  nro_cheque: '',
};

function EgresoForm({ inicial, onGuardar, onCancelar, guardando }) {
  const [form, setForm] = useState(inicial || FORM_VACIO);

  useEffect(() => {
    setForm(inicial || FORM_VACIO);
  }, [inicial]);

  const set = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

  const esProgramado = ['cheque', 'transferencia', 'a_pagar_efet'].includes(form.tipo);
  const esCheque = form.tipo === 'cheque';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.fecha || !form.importe || !form.tipo || !form.receptor) {
      Swal.fire({ icon: 'warning', title: 'Campos requeridos', text: 'Completá todos los campos obligatorios.' });
      return;
    }
    if (esProgramado && !form.fecha_cobro) {
      Swal.fire({ icon: 'warning', title: 'Fecha de cobro requerida', text: 'Para cheque/transferencia indicá la fecha de cobro.' });
      return;
    }
    if (esCheque && !form.nro_cheque) {
      Swal.fire({ icon: 'warning', title: 'Nro. cheque requerido', text: 'Ingresá el número de cheque.' });
      return;
    }
    const esProgramado2 = ['cheque', 'transferencia', 'a_pagar_efet'].includes(form.tipo);
    const payload = {
      fecha: form.fecha,
      importe: parseFloat(form.importe),
      tipo: form.tipo,
      receptor: form.receptor,
      fecha_cobro: esProgramado ? form.fecha_cobro : undefined,
      nro_cheque: esCheque ? form.nro_cheque : undefined,
      pagado: !esProgramado2 ? true : undefined,
    };
    onGuardar(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={form.fecha}
            onChange={(e) => set('fecha', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Importe <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={form.importe}
            onChange={(e) => set('importe', e.target.value)}
            placeholder="0.00"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo <span className="text-red-500">*</span>
          </label>
          <select
            value={form.tipo}
            onChange={(e) => set('tipo', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {TIPOS.map((t) => (
              <option key={t} value={t}>
                {tipoLabel(t)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Receptor <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.receptor}
            onChange={(e) => set('receptor', e.target.value)}
            placeholder="Proveedor, persona, etc."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        {esProgramado && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de cobro <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={form.fecha_cobro}
              onChange={(e) => set('fecha_cobro', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        )}
        {esCheque && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nro. Cheque <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.nro_cheque}
              onChange={(e) => set('nro_cheque', e.target.value)}
              placeholder="Ej: 00123456"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancelar}
          disabled={guardando}
          className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={guardando}
          className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-1 font-semibold"
        >
          <CheckIcon className="h-4 w-4" />
          {guardando ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

EgresoForm.propTypes = {
  inicial: PropTypes.object,
  onGuardar: PropTypes.func.isRequired,
  onCancelar: PropTypes.func.isRequired,
  guardando: PropTypes.bool,
};

function tipoBadge(tipo) {
  const colores = {
    cheque: 'bg-yellow-100 text-yellow-800',
    transferencia: 'bg-blue-100 text-blue-800',
    efectivo: 'bg-green-100 text-green-800',
    a_pagar_efet: 'bg-orange-100 text-orange-800',
    debito: 'bg-purple-100 text-purple-800',
    credito: 'bg-pink-100 text-pink-800',
  };
  return colores[tipo] || 'bg-gray-100 text-gray-700';
}

const GestionEgresos = ({ onClose }) => {
  const [egresos, setEgresos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Filtros
  const [filtroPagado, setFiltroPagado] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(10);

  // Modal formulario
  const [modoForm, setModoForm] = useState(null); // null | 'nuevo' | 'editar'
  const [egresoEditando, setEgresoEditando] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroPagado === 'pagado') params.pagado = true;
      if (filtroPagado === 'pendiente') params.pagado = false;
      if (filtroTipo) params.tipo = filtroTipo;
      if (fechaInicio) params.fecha_inicio = fechaInicio;
      if (fechaFin) params.fecha_fin = fechaFin;
      params.limite = 1000;

      const data = await obtenerEgresos(params);
      setEgresos(Array.isArray(data) ? data : []);
      setPaginaActual(1);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message });
    } finally {
      setLoading(false);
    }
  }, [filtroPagado, filtroTipo, fechaInicio, fechaFin]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const handleGuardar = async (payload) => {
    setGuardando(true);
    try {
      if (modoForm === 'editar' && egresoEditando) {
        await editarEgreso(egresoEditando.id, payload);
        Swal.fire({ icon: 'success', title: 'Egreso actualizado', timer: 1500, showConfirmButton: false });
      } else {
        await registrarEgresoFinanciero(payload);
        Swal.fire({ icon: 'success', title: 'Egreso registrado', timer: 1500, showConfirmButton: false });
      }
      setModoForm(null);
      setEgresoEditando(null);
      cargar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message });
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (egreso) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar egreso?',
      html: `<b>${egreso.receptor}</b> — ${formatearMoneda(egreso.importe)}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;
    try {
      await eliminarEgreso(egreso.id);
      Swal.fire({ icon: 'success', title: 'Eliminado', timer: 1200, showConfirmButton: false });
      cargar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message });
    }
  };

  const abrirEditar = (egreso) => {
    setEgresoEditando(egreso);
    setModoForm('editar');
  };

  const handleMarcarPagado = async (egreso) => {
    const respuesta = await Swal.fire({
      title: 'Marcar como pagado',
      html: `
        <p class="text-sm text-gray-600 mb-3"><b>${egreso.receptor}</b> — ${formatearMoneda(egreso.importe)}</p>
        <input id="metodo_pago" class="swal2-input" placeholder="Método de pago" value="${egreso.tipo || ''}" />
        <input id="observacion_pago" class="swal2-input" placeholder="Observación (opcional)" />
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Confirmar pago',
      confirmButtonColor: '#16a34a',
      cancelButtonText: 'Cancelar',
      preConfirm: () => ({
        metodo_pago: document.getElementById('metodo_pago')?.value || egreso.tipo,
        observacion_pago: document.getElementById('observacion_pago')?.value || null,
      }),
    });
    if (!respuesta.isConfirmed) return;
    try {
      await marcarEgresoPagado(egreso.id, {
        pagado: true,
        metodo_pago: respuesta.value.metodo_pago,
        observacion_pago: respuesta.value.observacion_pago || null,
      });
      Swal.fire({ icon: 'success', title: '¡Pagado!', timer: 1200, showConfirmButton: false });
      cargar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message });
    }
  };

  const initialFormEditar = egresoEditando
    ? {
        fecha: egresoEditando.fecha ? String(egresoEditando.fecha).slice(0, 10) : '',
        importe: String(egresoEditando.importe),
        tipo: egresoEditando.tipo,
        receptor: egresoEditando.receptor,
        fecha_cobro: egresoEditando.fecha_cobro ? String(egresoEditando.fecha_cobro).slice(0, 10) : '',
        nro_cheque: egresoEditando.nro_cheque || '',
      }
    : null;

  // Filtrado en memoria por término de búsqueda
  const egresosFiltrados = useMemo(() => {
    return egresos.filter((e) => {
      if (!busqueda.trim()) return true;
      const term = busqueda.toLowerCase().trim();
      const receptor = (e.receptor || '').toLowerCase();
      const tipo = tipoLabel(e.tipo || '').toLowerCase();
      const cheque = (e.nro_cheque || '').toLowerCase();
      const obs = (e.observacion_pago || '').toLowerCase();
      const monto = String(e.importe || '');
      return (
        receptor.includes(term) ||
        tipo.includes(term) ||
        cheque.includes(term) ||
        obs.includes(term) ||
        monto.includes(term)
      );
    });
  }, [egresos, busqueda]);

  // Cálculos de Paginación
  const totalFiltrado = useMemo(() => {
    return egresosFiltrados.reduce((acc, e) => acc + (Number(e.importe) || 0), 0);
  }, [egresosFiltrados]);

  const totalPaginas = Math.max(1, Math.ceil(egresosFiltrados.length / porPagina));
  const paginaValida = Math.min(paginaActual, totalPaginas);

  const egresosPaginados = useMemo(() => {
    const inicio = (paginaValida - 1) * porPagina;
    return egresosFiltrados.slice(inicio, inicio + porPagina);
  }, [egresosFiltrados, paginaValida, porPagina]);

  return (
    <div
      className="fixed inset-0 z-[1050] flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm overscroll-contain"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-auto flex flex-col max-h-[92vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con esquinas limpias por overflow-hidden */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-5 sm:px-6 py-4 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <BanknotesIcon className="w-5 h-5 sm:w-6 h-6 text-red-200 shrink-0" />
              <span>Gestión de Egresos</span>
            </h2>
            <p className="text-red-100 text-xs sm:text-sm mt-0.5">Administrá todos los gastos, pagos y cheques</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-red-800 rounded-xl p-1.5 sm:p-2 transition-colors"
            title="Cerrar modal"
          >
            <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Cuerpo scrolleable */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 overscroll-contain">
          {/* Formulario nuevo/editar */}
          {modoForm && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 shadow-2xs">
              <h3 className="text-sm font-bold text-blue-900 mb-3">
                {modoForm === 'editar' ? 'Editar egreso' : 'Nuevo egreso'}
              </h3>
              <EgresoForm
                inicial={modoForm === 'editar' ? initialFormEditar : FORM_VACIO}
                onGuardar={handleGuardar}
                onCancelar={() => {
                  setModoForm(null);
                  setEgresoEditando(null);
                }}
                guardando={guardando}
              />
            </div>
          )}

          {/* Barra de Filtros + Búsqueda + Rango de Fechas */}
          <div className="bg-gray-50/90 border border-gray-200 rounded-xl p-3 sm:p-4 mb-4 flex flex-col gap-3">
            {/* Fila 1: Selectores de Estado, Tipo y Buscador */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2">
                <select
                  value={filtroPagado}
                  onChange={(e) => {
                    setFiltroPagado(e.target.value);
                    setPaginaActual(1);
                  }}
                  className="w-full sm:w-auto border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs font-medium"
                >
                  <option value="todos">Todos los estados</option>
                  <option value="pendiente">Pendientes</option>
                  <option value="pagado">Pagados</option>
                </select>

                <select
                  value={filtroTipo}
                  onChange={(e) => {
                    setFiltroTipo(e.target.value);
                    setPaginaActual(1);
                  }}
                  className="w-full sm:w-auto border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs font-medium"
                >
                  <option value="">Todos los tipos</option>
                  {TIPOS.map((t) => (
                    <option key={t} value={t}>
                      {tipoLabel(t)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Buscador de texto */}
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => {
                    setBusqueda(e.target.value);
                    setPaginaActual(1);
                  }}
                  placeholder="Buscar gasto, receptor, nro. cheque, monto..."
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
                />
              </div>

              {/* Botón Nuevo egreso */}
              {!modoForm && (
                <button
                  type="button"
                  onClick={() => setModoForm('nuevo')}
                  className="w-full sm:w-auto flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors shadow-2xs shrink-0"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Nuevo egreso</span>
                </button>
              )}
            </div>

            {/* Fila 2: Filtro por Fecha (Desde / Hasta) y Total */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 pt-2.5 border-t border-gray-200">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                  <CalendarDaysIcon className="w-4 h-4 text-gray-500 shrink-0" />
                  <span>Filtrar por fecha:</span>
                </div>

                <div className="flex items-center gap-1.5">
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => {
                      setFechaInicio(e.target.value);
                      setPaginaActual(1);
                    }}
                    title="Fecha desde"
                    className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
                  />
                  <span className="text-gray-400 text-xs">a</span>
                  <input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => {
                      setFechaFin(e.target.value);
                      setPaginaActual(1);
                    }}
                    title="Fecha hasta"
                    className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
                  />
                </div>

                {(fechaInicio || fechaFin) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFechaInicio('');
                      setFechaFin('');
                      setPaginaActual(1);
                    }}
                    className="text-xs text-red-600 hover:text-red-800 font-semibold underline px-1.5 py-0.5"
                  >
                    Limpiar fechas
                  </button>
                )}
              </div>

              {/* Total filtrado */}
              <div className="text-right text-xs sm:text-sm font-medium text-gray-600">
                Total filtrado:{' '}
                <span className="text-red-600 font-bold text-sm sm:text-base">
                  {formatearMoneda(totalFiltrado)}
                </span>
                <span className="text-gray-400 text-xs ml-1.5">
                  ({egresosFiltrados.length} {egresosFiltrados.length === 1 ? 'registro' : 'registros'})
                </span>
              </div>
            </div>
          </div>

          {/* Tabla de egresos */}
          {loading ? (
            <div className="text-center py-12 text-gray-500 text-sm">Cargando egresos...</div>
          ) : egresosFiltrados.length === 0 ? (
            <div className="text-center py-12 text-gray-400 bg-gray-50/50 rounded-xl border border-gray-100">
              <BanknotesIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm font-medium">No se encontraron egresos con los filtros seleccionados</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-2xs">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-200">
                      <th className="text-left px-3.5 py-2.5 font-semibold text-gray-700 text-xs">Fecha</th>
                      <th className="text-left px-3.5 py-2.5 font-semibold text-gray-700 text-xs">Receptor</th>
                      <th className="text-left px-3.5 py-2.5 font-semibold text-gray-700 text-xs">Tipo</th>
                      <th className="text-right px-3.5 py-2.5 font-semibold text-gray-700 text-xs">Importe</th>
                      <th className="text-center px-3.5 py-2.5 font-semibold text-gray-700 text-xs">Estado</th>
                      <th className="text-left px-3.5 py-2.5 font-semibold text-gray-700 text-xs">F. cobro</th>
                      <th className="text-center px-3.5 py-2.5 font-semibold text-gray-700 text-xs">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {egresosPaginados.map((e) => (
                      <tr key={e.id} className="hover:bg-blue-50/40 transition-colors">
                        <td className="px-3.5 py-2.5 text-gray-700 whitespace-nowrap text-xs sm:text-sm">
                          {e.fecha ? new Date(e.fecha).toLocaleDateString('es-AR') : '-'}
                        </td>
                        <td className="px-3.5 py-2.5 text-gray-800 font-medium max-w-[180px] truncate text-xs sm:text-sm">
                          {e.receptor}
                        </td>
                        <td className="px-3.5 py-2.5 whitespace-nowrap">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${tipoBadge(e.tipo)}`}>
                            {tipoLabel(e.tipo)}
                          </span>
                          {e.nro_cheque && (
                            <span className="ml-1 text-xs text-gray-500 font-mono">#{e.nro_cheque}</span>
                          )}
                        </td>
                        <td className="px-3.5 py-2.5 text-right font-bold text-red-600 whitespace-nowrap text-xs sm:text-sm">
                          {formatearMoneda(e.importe)}
                        </td>
                        <td className="px-3.5 py-2.5 text-center whitespace-nowrap">
                          {e.pagado ? (
                            <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
                              Pagado
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-800">
                              Pendiente
                            </span>
                          )}
                        </td>
                        <td className="px-3.5 py-2.5 text-gray-600 whitespace-nowrap text-xs sm:text-sm">
                          {e.fecha_cobro ? new Date(e.fecha_cobro).toLocaleDateString('es-AR') : '-'}
                        </td>
                        <td className="px-3.5 py-2.5 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1.5">
                            {!e.pagado && (
                              <button
                                type="button"
                                onClick={() => handleMarcarPagado(e)}
                                className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
                                title="Marcar como pagado"
                              >
                                <BanknotesIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => abrirEditar(e)}
                              className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                              title="Editar"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEliminar(e)}
                              className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                              title="Eliminar"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Controles de Paginación */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-200 mt-4 text-xs sm:text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span>Mostrar:</span>
                  <select
                    value={porPagina}
                    onChange={(e) => {
                      setPorPagina(Number(e.target.value));
                      setPaginaActual(1);
                    }}
                    className="border border-gray-300 rounded-lg px-2 py-1 bg-white text-xs sm:text-sm font-semibold focus:ring-2 focus:ring-blue-500 shadow-2xs"
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-gray-500 ml-1.5">
                    ({egresosFiltrados.length === 0 ? 0 : (paginaValida - 1) * porPagina + 1} -{' '}
                    {Math.min(paginaValida * porPagina, egresosFiltrados.length)} de {egresosFiltrados.length})
                  </span>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
                    disabled={paginaValida <= 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white flex items-center gap-1 font-semibold transition text-xs sm:text-sm"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    <span>Anterior</span>
                  </button>

                  <span className="px-3 py-1 font-bold text-gray-800 text-xs sm:text-sm">
                    {paginaValida} / {totalPaginas}
                  </span>

                  <button
                    type="button"
                    onClick={() => setPaginaActual((p) => Math.min(totalPaginas, p + 1))}
                    disabled={paginaValida >= totalPaginas}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white flex items-center gap-1 font-semibold transition text-xs sm:text-sm"
                  >
                    <span>Siguiente</span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

GestionEgresos.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default GestionEgresos;
